/*
  GEMDRAW — Accordion

  Used by the Help Center FAQ. Built on real buttons with aria-expanded and
  aria-controls rather than on a details element, because the Help Center needs
  behavior a disclosure does not give: deep linking into a specific question,
  an optional single-open mode, and a height transition.

  Two details matter for assistive technology:

  - A collapsed panel is genuinely unavailable, not merely clipped. The inner
    box is set to visibility: hidden while closed, so a screen reader does not
    walk through the text of every closed answer.
  - The transition is on grid-template-rows rather than on height, so the panel
    animates to its natural size without the script measuring anything and
    without a fixed height that would clip long answers.

  Without JavaScript every panel renders open, so no answer is ever trapped
  behind a control that cannot run.
*/

(function () {
  'use strict';

  const OPEN = 'data-gd-open';

  function panelFor(button) {
    const id = button.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  function setOpen(button, open) {
    const panel = panelFor(button);
    if (!panel) return;

    button.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) panel.setAttribute(OPEN, '');
    else panel.removeAttribute(OPEN);
  }

  function closeSiblings(root, except) {
    if (root.dataset.gdAccordionSingle !== 'true') return;
    root.querySelectorAll('[data-gd-accordion-trigger]').forEach(function (button) {
      if (button !== except) setOpen(button, false);
    });
  }

  function onClick(event) {
    const button = event.target.closest('[data-gd-accordion-trigger]');
    if (!button) return;

    const root = button.closest('[data-gd-accordion]');
    const isOpen = button.getAttribute('aria-expanded') === 'true';

    if (!isOpen && root) closeSiblings(root, button);
    setOpen(button, !isOpen);
  }

  /*
    Opens the question a URL points at, whether the hash names the question
    itself or the category it sits in, and brings it into view under the pinned
    shell. Runs on load and on every later hash change.
  */
  function openFromHash() {
    const hash = window.location.hash;
    if (!hash || hash.length < 2) return;

    let target;
    try {
      target = document.querySelector(hash);
    } catch (error) {
      return;
    }
    if (!target) return;

    const trigger = target.matches('[data-gd-accordion-trigger]')
      ? target
      : target.querySelector('[data-gd-accordion-trigger]');

    if (trigger) {
      const root = trigger.closest('[data-gd-accordion]');
      if (root) closeSiblings(root, trigger);
      setOpen(trigger, true);
    }
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-gd-accordion]').forEach(function (root) {
      if (root.dataset.gdAccordionBound === 'true') return;
      root.dataset.gdAccordionBound = 'true';
      root.addEventListener('click', onClick);

      /* The markup ships open so the content is readable without scripting.
         Now that the script is running, collapse everything except whatever a
         block was configured to leave open. */
      root.querySelectorAll('[data-gd-accordion-trigger]').forEach(function (button) {
        setOpen(button, button.dataset.gdOpenByDefault === 'true');
      });
    });

    openFromHash();
  }

  function start() {
    init(document);
    window.addEventListener('hashchange', openFromHash);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  /* Selecting a question block in the theme editor opens it, so the merchant
     sees what they are editing. */
  document.addEventListener('shopify:block:select', function (event) {
    const trigger = event.target.querySelector('[data-gd-accordion-trigger]');
    if (!trigger) return;
    const root = trigger.closest('[data-gd-accordion]');
    if (root) closeSiblings(root, trigger);
    setOpen(trigger, true);
  });
})();
